import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Users2, UserSquare2, AlertTriangle, CheckCircle2, Phone } from "lucide-react";
import { AdminShell } from "@/components/admin/AdminShell";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LogContactDialog, BUCKET_BADGE, BUCKET_LABEL } from "@/pages/admin/LogContactDialog";
import { api } from "@/lib/api";
import { hasAdminSession } from "@/lib/session";

const STAGES = ["New", "Contacted", "Interested", "Negotiating", "Closed Won", "Closed Lost"];

function Kpi({ icon: Icon, label, value, sub, accent, testid, onClick, active }) {
  return (
    <Card
      data-testid={testid}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
      className={`p-4 transition-transform ${onClick ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]" : ""} ${active ? "ring-2 ring-[hsl(var(--primary))]" : ""}`}
    >
      <div className="flex items-center justify-between"><span className="text-xs text-muted-foreground uppercase tracking-wide">{label}</span><div className={`h-8 w-8 rounded-lg flex items-center justify-center ${accent}`}><Icon className="h-4 w-4" /></div></div>
      <div className="font-mono text-3xl mt-2">{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{sub}</div>
    </Card>
  );
}

const VALID_TABS = ["customer", "lead", "delays"];

export default function DailyTasks() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = VALID_TABS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "customer";
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(initialTab);
  const [logEntity, setLogEntity] = useState(null);
  const [logOpen, setLogOpen] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/crm/daily-tasks"); setData(data); }
    catch { navigate("/admin/login"); }
  }, [navigate]);
  useEffect(() => { if (!hasAdminSession()) { navigate("/admin/login"); return; } load(); }, [load, navigate]);

  // keep tab in sync with the URL (so dashboard deep-links open the right tab)
  useEffect(() => {
    const t = searchParams.get("tab");
    if (VALID_TABS.includes(t) && t !== tab) setTab(t);
    // eslint-disable-next-line
  }, [searchParams]);

  const changeTab = (t) => { setTab(t); setSearchParams(t === "customer" ? {} : { tab: t }, { replace: true }); };

  const openLog = (type, id, name, ref) => { setLogEntity({ type, id, name, ref }); setLogOpen(true); };

  if (!data) return <AdminShell title="Daily Tasks"><div className="py-24 text-center text-muted-foreground">Loading...</div></AdminShell>;
  const c = data.cards;

  return (
    <AdminShell title="Daily Tasks">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Kpi testid="dt-card-customer" icon={Users2} label="Customer Follow-Ups" value={c.customer_followups.count} sub={`${c.customer_followups.overdue} overdue · ${c.customer_followups.due_today} due today`} accent="bg-[#EAF1F7] text-[#1E3E5C]" onClick={() => changeTab("customer")} active={tab === "customer"} />
        <Kpi testid="dt-card-lead" icon={UserSquare2} label="Lead Follow-Ups" value={c.lead_followups.count} sub={`${c.lead_followups.overdue} overdue · ${c.lead_followups.due_today} due today`} accent="bg-[#EDE7F7] text-[#4B2E7A]" onClick={() => changeTab("lead")} active={tab === "lead"} />
        <Kpi testid="dt-card-delays" icon={AlertTriangle} label="Delay Flags" value={c.delay_flags.count} sub={`${c.delay_flags.critical} critical`} accent="bg-[#FBE7E7] text-[#7A1F1F]" onClick={() => changeTab("delays")} active={tab === "delays"} />
        <Kpi testid="dt-card-contacted" icon={CheckCircle2} label="Contacted Today" value={c.contacted_today.count} sub={`of ${c.contacted_today.todays_list} today's list`} accent="bg-[#E7F3EA] text-[#1F5B2E]" />
      </div>

      <Tabs value={tab} onValueChange={changeTab} className="mt-6">
        <TabsList>
          <TabsTrigger data-testid="dt-tab-customer" value="customer">Customer Follow-Ups {c.customer_followups.count}</TabsTrigger>
          <TabsTrigger data-testid="dt-tab-lead" value="lead">Lead Follow-Ups {c.lead_followups.count}</TabsTrigger>
          <TabsTrigger data-testid="dt-tab-delays" value="delays">Delays {c.delay_flags.count}</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "customer" && (
        <Card className="mt-4 overflow-hidden"><div className="overflow-x-auto">
          <Table data-testid="dt-customer-table">
            <TableHeader><TableRow className="bg-[#F4EBDD]"><TableHead>Priority</TableHead><TableHead>Customer</TableHead><TableHead>Salesman</TableHead><TableHead>WhatsApp</TableHead><TableHead>Last</TableHead><TableHead>Next Due</TableHead><TableHead>Freq</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.customer_followups.map((r) => (
                <TableRow key={r.id} className={r.followup_bucket === "overdue" ? "bg-[#FCEDED]" : ""}>
                  <TableCell><Badge className={BUCKET_BADGE[r.followup_bucket]}>{BUCKET_LABEL[r.followup_bucket]}</Badge></TableCell>
                  <TableCell><div className="font-medium">{r.shop_name}</div><div className="text-xs text-muted-foreground">{r.contact_person || "—"}</div></TableCell>
                  <TableCell>{r.salesman_name || "—"}</TableCell>
                  <TableCell className="font-mono text-sm">{r.phone || "—"}</TableCell>
                  <TableCell className="text-sm">{r.last_contact || "—"}</TableCell>
                  <TableCell className="text-sm">{r.next_contact}{r.days_overdue > 0 ? <span className="text-destructive"> · {r.days_overdue}d</span> : null}</TableCell>
                  <TableCell className="text-sm">{r.frequency_days}d</TableCell>
                  <TableCell className="text-right"><Button data-testid="dt-log-customer" size="sm" variant="secondary" className="gap-1" onClick={() => openLog("customer", r.id, r.shop_name)}><Phone className="h-3.5 w-3.5" /> Log</Button></TableCell>
                </TableRow>
              ))}
              {data.customer_followups.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No customer follow-ups.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div></Card>
      )}

      {tab === "lead" && (
        <Card className="mt-4 overflow-hidden"><div className="overflow-x-auto">
          <Table data-testid="dt-lead-table">
            <TableHeader><TableRow className="bg-[#F4EBDD]"><TableHead>Priority</TableHead><TableHead>Lead</TableHead><TableHead>Stage</TableHead><TableHead>WhatsApp</TableHead><TableHead>Salesman</TableHead><TableHead>Next Due</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.lead_followups.map((r) => (
                <TableRow key={r.id} className={bucketRow(r)}>
                  <TableCell><Badge className={BUCKET_BADGE[r.followup_bucket]}>{BUCKET_LABEL[r.followup_bucket]}</Badge></TableCell>
                  <TableCell><div className="font-medium">{r.company}</div><div className="text-xs text-muted-foreground">{r.contact_person} · {r.lead_no}</div></TableCell>
                  <TableCell><Badge variant="secondary">{r.stage}</Badge></TableCell>
                  <TableCell className="font-mono text-sm">{r.whatsapp || "—"}</TableCell>
                  <TableCell>{r.salesman_name || "—"}</TableCell>
                  <TableCell className="text-sm">{r.next_contact}{r.days_overdue > 0 ? <span className="text-destructive"> · {r.days_overdue}d</span> : null}</TableCell>
                  <TableCell className="text-right"><Button data-testid="dt-log-lead" size="sm" variant="secondary" className="gap-1" onClick={() => openLog("lead", r.id, r.company, r.lead_no)}><Phone className="h-3.5 w-3.5" /> Log</Button></TableCell>
                </TableRow>
              ))}
              {data.lead_followups.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No lead follow-ups.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div></Card>
      )}

      {tab === "delays" && (
        <div className="mt-4 space-y-3" data-testid="dt-delays-list">
          {data.delays.map((d, i) => (
            <Card
              key={`${d.entity_kind || "delay"}-${d.entity_ref || i}-${d.type}`}
              data-testid="dt-delay-card"
              onClick={() => d.entity_kind && d.entity_ref && openLog(d.entity_kind, d.entity_ref, d.entity, null)}
              className={`p-4 border-l-4 ${d.severity === "critical" ? "border-l-[#7A1F1F]" : "border-l-[#8E6F33]"} ${d.entity_kind ? "cursor-pointer hover:bg-[#F7F0E6]" : ""}`}
            >
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className={`h-4 w-4 ${d.severity === "critical" ? "text-[#7A1F1F]" : "text-[#8E6F33]"}`} />
                  <div><div className="font-medium">{d.type}</div><div className="text-sm text-muted-foreground">{d.entity} · {d.action}</div></div>
                </div>
                <div className="text-right flex items-center gap-3">
                  <div><Badge className={d.severity === "critical" ? BUCKET_BADGE.overdue : BUCKET_BADGE.due_today}>{d.severity}</Badge><div className="text-xs text-muted-foreground mt-1">{d.days_overdue}d overdue · {d.responsible}</div></div>
                  {d.entity_kind && <Button data-testid="dt-delay-log" size="sm" variant="secondary" className="gap-1" onClick={(e) => { e.stopPropagation(); openLog(d.entity_kind, d.entity_ref, d.entity, null); }}><Phone className="h-3.5 w-3.5" /> Log</Button>}
                </div>
              </div>
            </Card>
          ))}
          {data.delays.length === 0 && <Card className="p-8 text-center text-muted-foreground">No delays flagged. </Card>}
        </div>
      )}

      <LogContactDialog open={logOpen} onOpenChange={setLogOpen} entity={logEntity} stages={logEntity?.type === "lead" ? STAGES : ["active", "blocked"]} onSaved={load} />
    </AdminShell>
  );
}

function bucketRow(r) { return r.followup_bucket === "overdue" ? "bg-[#FCEDED]" : ""; }
